/**
 * Theme resolution — the framework-agnostic core.
 *
 * Mirrors the logic already proven in the prototypes' inline <script>
 * (souk-storefront.html, souk-dashboard.html, souk-admin.html): an explicit
 * `data-theme` attribute wins; absent that, `prefers-color-scheme` decides;
 * the explicit choice is persisted so it survives a reload.
 *
 * Kept dependency-free (no React import) so the exact same function can run
 * inside the no-flash bootstrap script (theme-script.ts, executed before any
 * framework has loaded) and inside the React hook (use-theme.ts) without
 * duplicating the decision logic in two places that could drift apart.
 */
export type ThemeMode = 'light' | 'dark';
export type ThemePreference = ThemeMode | 'system';

export const THEME_STORAGE_KEY = 'ecom-theme';
export const THEME_ATTRIBUTE = 'data-theme';

function isThemeMode(value: string | null | undefined): value is ThemeMode {
  return value === 'light' || value === 'dark';
}

/**
 * Reads the persisted explicit choice, if any.
 *
 * Never throws: storage can be disabled (private browsing, quota), absent
 * (server-side rendering, before hydration), or the injected fake in a test.
 * All three must return null, not crash the render.
 */
export function readStoredTheme(storage?: Pick<Storage, 'getItem'>): ThemeMode | null {
  try {
    const target = storage ?? globalThis.localStorage;
    const value: string | null | undefined = target.getItem(THEME_STORAGE_KEY);
    return isThemeMode(value) ? value : null;
  } catch {
    return null;
  }
}

export function persistTheme(mode: ThemeMode, storage?: Pick<Storage, 'setItem'>): void {
  try {
    const target = storage ?? globalThis.localStorage;
    target.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // Degrades to "applies for this page load only" — acceptable. Throwing
    // here would break the toggle entirely just because storage is denied.
  }
}

/**
 * Resolves the mode actually in effect right now: the explicit attribute if
 * set, otherwise the OS preference.
 */
export function resolveEffectiveTheme(
  explicitAttribute: string | null,
  prefersDark: boolean,
): ThemeMode {
  if (isThemeMode(explicitAttribute)) return explicitAttribute;
  return prefersDark ? 'dark' : 'light';
}
